import React from 'react';
import ReactEcharts from 'echarts-for-react';

const DEFAULT_REMAINING = '#00685e';
const DEFAULT_OTHER = '#e8edf2';
const DEFAULT_MUTED = '#667085';
const DEFAULT_CENTER_STRONG = '#2b2d63';

function radiusForHeight(height) {
  const h = typeof height === 'number' && height > 0 ? height : 300;
  if (h >= 220) return ['56%', '80%'];
  if (h >= 170) return ['50%', '74%'];
  if (h >= 140) return ['44%', '66%'];
  if (h >= 120) return ['42%', '62%'];
  return ['38%', '58%'];
}

function centerTextForCenterLines(centerLines, height) {
  const h = typeof height === 'number' && height > 0 ? height : 300;
  const roomy = h >= 220;
  const medium = h >= 170 && !roomy;
  const line1 = String(centerLines.line1 ?? '');
  const line3Raw = centerLines.line3;
  const hasLine3 = line3Raw != null && line3Raw !== '';
  const line3 = hasLine3 ? `${centerLines.line3Prefix || ''}${line3Raw}` : null;
  const twoLine = !hasLine3 && centerLines.line2;
  const longNum = line1.length > 4;

  return {
    line1,
    line2: centerLines.line2,
    line3,
    line1Style: {
      color: DEFAULT_CENTER_STRONG,
      fontSize: roomy ? 34 : medium ? (longNum ? 26 : 30) : twoLine ? 28 : 22,
      fontWeight: 700,
      lineHeight: roomy ? '38px' : medium ? '32px' : twoLine ? '30px' : '24px',
    },
    line2Style: {
      color: '#667085',
      fontSize: roomy ? 14 : medium ? 12 : 10,
      fontWeight: 600,
      lineHeight: roomy ? '18px' : medium ? '15px' : '12px',
      whiteSpace: 'nowrap',
    },
    line3Style: line3
      ? {
          color: '#98a2b3',
          fontSize: roomy ? 13 : medium ? 11 : 9,
          fontWeight: 500,
          lineHeight: roomy ? '16px' : medium ? '13px' : '11px',
          whiteSpace: 'nowrap',
        }
      : null,
  };
}

export default function SubscriptionChart({ summary, centerLabel, centerLines, height = 300, sliceColors, slices }) {
  const { total = 0, remaining = 0 } = summary;
  const colorRemaining = sliceColors?.remaining ?? DEFAULT_REMAINING;
  const colorOther = sliceColors?.other ?? DEFAULT_OTHER;

  const data =
    slices && slices.length > 0
      ? slices.map((slice) => ({
          value: slice.value >= 0 ? slice.value : 0,
          color: slice.color,
          name: slice.name,
        }))
      : [
          { value: remaining >= 0 ? remaining : 0, color: colorRemaining },
          { value: total - remaining >= 0 ? total - remaining : 0, color: colorOther },
        ];

  let centerText;

  if (centerLines && centerLines.line1 != null) {
    centerText = centerTextForCenterLines(centerLines, height);
  } else if (centerLabel) {
    centerText = {
      line1: remaining,
      line2: centerLabel,
      line1Style: {
        color: DEFAULT_CENTER_STRONG,
        fontWeight: 700,
        fontSize: height <= 220 ? 24 : 28,
        lineHeight: '32px',
      },
      line2Style: {
        color: DEFAULT_MUTED,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '16px',
      },
    };
  } else {
    centerText = {
      line1: remaining,
      line2: 'remaining',
      line3: `out of ${total}`,
      line1Style: {
        color: DEFAULT_CENTER_STRONG,
        fontWeight: 600,
        fontSize: height <= 220 ? 26 : 32,
        lineHeight: height <= 220 ? '30px' : '36px',
      },
      line2Style: {
        color: DEFAULT_MUTED,
        fontSize: 11,
        lineHeight: '14px',
      },
      line3Style: {
        color: DEFAULT_MUTED,
        fontSize: 11,
        lineHeight: '16px',
      },
    };
  }

  const option = {
    series: [
      {
        type: 'pie',
        radius: radiusForHeight(height),
        center: ['50%', '51%'],
        startAngle: 90,
        clockwise: true,
        data,
        labelLine: { show: false },
        label: { show: false },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
          color(params) {
            return params.data.color;
          },
        },
        emphasis: {
          itemStyle: { shadowBlur: 0 },
          scale: false,
        },
        top: 0,
      },
    ],
  };

  return (
    <div className="summary-chart" style={{ position: 'relative' }}>
      <ReactEcharts option={option} notMerge lazyUpdate style={{ height }} />
      <div
        className="summary-chart-center"
        style={{
          position: 'absolute',
          left: '50%',
          top: '51%',
          transform: 'translate(-50%, -50%)',
          width: '52%',
          maxWidth: 108,
          textAlign: 'center',
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <div style={centerText.line1Style}>{centerText.line1}</div>
        {centerText.line2 && <div style={centerText.line2Style}>{centerText.line2}</div>}
        {centerText.line3 && centerText.line3Style && <div style={centerText.line3Style}>{centerText.line3}</div>}
      </div>
    </div>
  );
}
